/**
 * Vrai quand l'appareil est piloté par un **pointeur fin** — souris ou pavé
 * tactile — plutôt qu'au doigt.
 *
 * Sert à choisir l'affordance de suppression d'une entrée du journal :
 * glissement vers la gauche au doigt (`useSwipeToDelete`), bouton visible à la
 * souris. Demandé après essai — glisser une ligne à la souris est un geste que
 * personne ne devine, alors qu'au doigt c'est celui d'une liste iOS.
 *
 * 🔴 **`(hover: hover) and (pointer: fine)`, les deux ensemble.** `pointer:
 * fine` seul est vrai pour un stylet, qui n'a pas de survol et pour lequel un
 * petit bouton reste difficile à viser ; `hover: hover` seul l'est aussi pour
 * certains navigateurs mobiles qui simulent le survol. Le couple ne décrit que
 * la souris et le pavé tactile, c'est-à-dire exactement le cas où un bouton de
 * 44 px est plus simple qu'un geste.
 *
 * ⚠️ **Réévalué au changement, pas seulement au montage.** Un iPad auquel on
 * branche un clavier-trackpad bascule d'un mode à l'autre sans recharger la
 * page ; sans l'abonnement, l'écran garderait l'affordance du mode précédent
 * jusqu'au prochain démarrage.
 */

import { useEffect, useState } from 'react';

const QUERY = '(hover: hover) and (pointer: fine)';

export function useFinePointer(): boolean {
  const [fine, setFine] = useState(() => window.matchMedia(QUERY).matches);

  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setFine(event.matches);
    };
    /*
     * ⚠️ **Aucun `setFine` synchrone ici**, seulement l'abonnement : appeler
     * `setState` dans le corps d'un effet enchaîne un rendu pour rien, et l'état
     * initial est déjà lu paresseusement juste au-dessus. Un basculement entre
     * ce premier rendu et l'abonnement — brancher un trackpad dans la même
     * milliseconde — n'existe pas en pratique.
     */
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  return fine;
}
